import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { PayService } from '../service/pay.service';

interface ResetTokenForm {
  identifier: FormControl<string>;
}

interface ResetPasswordForm {
  token: FormControl<string>;
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}

interface ResetPasswordPayload {
  token: string;
  password: string;
  confirmPassword: string;
}

interface ResetTokenPayload {
  identifier: string;
}

const passwordMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const password = group.get('password')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;
  return password === confirmPassword ? null : { mismatch: true };
};


@Component({
  selector: 'app-rest-login',
  templateUrl: './rest-login.component.html',
  styleUrls: ['./rest-login.component.css'],
  standalone: false
})
export class RestLoginComponent implements OnInit {
  resetTokenForm!: FormGroup<ResetTokenForm>;
  resetPasswordForm!: FormGroup<ResetPasswordForm>;

  showTokenForm = true;
  showPasswordForm = false;

  validation = false;
  numberchange = false;
  lengthchange = false;
  symbolechange = false;
  letterchange = false;
  capitalchange = false;

  passwordChange = false;
  loader = false;
  emailNotFound = false;
  phoneNotFound = false;

  constructor(private userService: PayService) { }

  ngOnInit(): void {
    this.initResetTokenForm();
    this.initResetPasswordForm();
  }

  initResetTokenForm(): void {
    this.resetTokenForm = new FormGroup<ResetTokenForm>({
      identifier: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          // Email ou numéro CM (65x xxxxxx, 69/66/67/64 xxxxxxx)
          Validators.pattern('[^ @]*@[^ @]*[.][^ @]{2,}|(^(65)[0-9]{1}[0-9]{6}|^(69|66|67|64)[0-9]{7})$')
        ]
      })
    });
  }

  initResetPasswordForm(): void {
    this.resetPasswordForm = new FormGroup<ResetPasswordForm>({
      token: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      password: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&].{8,}')
        ]
      }),
      confirmPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&].{8,}')
        ]
      }),
    }, { validators: passwordMatchValidator });
  }

  sendEmailToken(formValue?: ResetTokenPayload): void {
    const payload = formValue ?? this.resetTokenForm.getRawValue();
    const trimmedIdentifier = (payload.identifier || '').trim();

    // normalize identifier by trimming leading/trailing spaces
    if (payload.identifier !== trimmedIdentifier) {
      this.resetTokenForm.patchValue({ identifier: trimmedIdentifier }, { emitEvent: false });
    }

    if (!trimmedIdentifier || this.resetTokenForm.invalid) {
      this.resetTokenForm.markAllAsTouched();
      return;
    }

    this.loader = true;
    this.userService.requestPasswordReset(trimmedIdentifier).pipe(take(1)).subscribe(res => {
      setTimeout(() => {
        this.loader = false;
        if (res.success) {
          this.showTokenForm = false;
          this.showPasswordForm = true;
        } else {
          this.flagIdentifierNotFound(trimmedIdentifier);
        }
      }, 300);
    }, _ => {
      this.loader = false;
      this.flagIdentifierNotFound(trimmedIdentifier);
    });
  }
  sendEmailTokenAndPassord(formValue?: ResetPasswordPayload): void {
    if (this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    const payload = formValue ?? this.resetPasswordForm.getRawValue();
    this.loader = true;
    this.userService.confirmPasswordReset({ token: payload.token, password: payload.password }).pipe(take(1)).subscribe(res => {
      if (res.success) {
        this.passwordChange = true;
        this.resetPasswordForm.reset({ token: '', password: '', confirmPassword: '' });
      }
      setTimeout(() => {
        this.passwordChange = false;
        this.loader = false;
      }, 1500);
    }, _ => {
      this.loader = false;
    });
  }


  validateValue(passwordValue: string): void {
    const value = passwordValue || '';

    this.letterchange = /[a-z]/.test(value);
    this.capitalchange = /[A-Z]/.test(value);
    this.numberchange = /[0-9]/.test(value);
    this.lengthchange = value.length >= 8 && value.length < 17;
    this.symbolechange = /[$@$!%*?&]/.test(value);
  }

  showvalidationpart() {
    this.validation = true;
  }
  hidevalidationpart() {
    this.validation = false;

  }

  private flagIdentifierNotFound(identifier: string): void {
    const looksLikeEmail = identifier.includes('@');
    this.emailNotFound = looksLikeEmail;
    this.phoneNotFound = !looksLikeEmail;
    setTimeout(() => {
      this.emailNotFound = false;
      this.phoneNotFound = false;
    }, 3000);
  }

  get identifier() { return this.resetTokenForm.controls.identifier; }
  get password() { return this.resetPasswordForm.controls.password; }
  get confirmPassword() { return this.resetPasswordForm.controls.confirmPassword; }
  get token() { return this.resetPasswordForm.controls.token; }


}
